"use client";

import { createContext, forwardRef, useContext } from "react";
import { TopPanel } from "@/components/top-panel";
import { useIsDesktop } from "@/lib/use-is-desktop";
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

/**
 * ЕДИНАЯ шапка экрана-вкладки: название вкладки крупно, под ним строка-описание,
 * справа — контролы этого экрана (выбор кабинета, период, тумблеры, кнопки).
 *
 * До 03.08 каждый модуль рисовал шапку по-своему: где-то h1 20px bold, где-то
 * 22px Benzin, где-то вместо заголовка мелкий «ОТДЕЛ ПРОДАЖ», а
 * где-то заголовка не было вовсе. Олег попросил привести всё к одному виду —
 * новые экраны берут ТОЛЬКО этот компонент.
 *
 * Заголовок — <h1>: глобальный стиль в globals.css уже даёт ему Benzin.
 * Выравнивание по ВЕРХУ (items-start), а не по низу: у экранов с высокими
 * контролами (даты «День»/«Период» в «Отчёте») заголовок иначе съезжал вниз и
 * отступ от верхнего бара переставал совпадать с соседними вкладками.
 */
/** «Заголовок экрана уже нарисован выше» — ставит страница отдела.
 *
 *  ⚠️ ЗАЧЕМ КОНТЕКСТ, А НЕ ПРОП. Внутри отдела название раздела печатает сама
 *  страница, и каждый модуль печатал ЕЩЁ ОДИН заголовок — часто с другим текстом
 *  («Боты» в меню, «Управление ботами» на экране). Чинить это пропом `embedded`
 *  пришлось бы в каждом модуле по очереди, и каждый новый экран заводил бы
 *  ошибку заново. Контекст выключает дублирующий заголовок разом и навсегда
 *  (Максим, 27.08.2026). */
const ScreenTitleShown = createContext(false);

export function ScreenTitleProvider({ children }: { children: ReactNode }) {
  return <ScreenTitleShown.Provider value>{children}</ScreenTitleShown.Provider>;
}

export function TabHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  /** Без заголовка блок рисует только контролы. Нужно, когда экран вставлен в
   *  страницу отдела: там название раздела и ряд вкладок уже нарисованы выше, и
   *  собственный заголовок модуля их дублировал (Максим, 27.08.2026). */
  title?: string;
  subtitle?: ReactNode;
  /** Контролы экрана — прижимаются вправо, переносятся на свою строку на узком экране. */
  actions?: ReactNode;
  className?: string;
}) {
  const titleShownAbove = useContext(ScreenTitleShown);
  const showTitle = !!title && !titleShownAbove;
  const isDesktop = useIsDesktop();
  return (
    // На широком экране заголовок и контролы стоят в ОДНУ строку: подпись сжимается
    // и переносится, а ряд контролов занимает свободное место справа вверху (раньше
    // длинная подпись выталкивала его на отдельную строку под заголовком).
    // На узком (max-md) — переносим, иначе контролы сплющивают заголовок.
    // Правый отступ — место под панель оболочки (уведомления, чат, настройки,
    // профиль). Резервирует ТОЛЬКО шапка с заголовком: вложенная шапка модуля
    // внутри отдела стоит ниже строки заголовка, и ей уступать нечему.
    <div className={`flex items-start justify-between gap-4 max-md:flex-wrap ${className ?? ""}`}>
      <div className="min-w-0 flex-1">
        {showTitle && (
          <h1 className="text-[20px] font-bold leading-tight text-[var(--text)]">{title}</h1>
        )}
        {showTitle && subtitle && (
          <p className="mt-0.5 text-[12px] text-[var(--textMuted)]">
            {subtitle}
          </p>
        )}
      </div>
      {/* ml-auto + justify-end: при переносе на свою строку (длинная подпись,
          узкий экран) контролы всё равно остаются у правого края, а не
          прыгают влево под заголовок.
          ⚠️ ПАНЕЛЬ ПОРТАЛА — ПОСЛЕДНЕЙ В ЭТОМ ЖЕ РЯДУ (Максим, 28.08.2026:
          «там это находится прямо в теле страницы»). Она в потоке, поэтому
          контролы экрана не заезжают под неё и ничего резервировать не нужно.
          Показываем только вместе с заголовком: внутри отдела шапок две
          (страница отдела + вложенный модуль), иначе панель задвоится. */}
      {actions && (
        // Потолок ширины: без него ряд контролов забирал всё место и сжимал
        // заголовок в две строки («Аналитика и отчёты» на 1440). Теперь сначала
        // переносятся контролы — их и так несколько рядов, — а заголовок
        // остаётся одной строкой.
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2 lg:max-w-[52%] max-md:w-full">{actions}</div>
      )}
      {/* ⚠️ ПАНЕЛЬ — СОСЕД РЯДА КОНТРОЛОВ, А НЕ ЕГО ЧАСТЬ. Внутри ряда её
          сносило на вторую строку вместе с переносом контролов («Аналитика»:
          кабинет, «Данные», «Готово», «Период» — в одну строку не влезают).
          Отдельным элементом она держится у правого края первой строки, а
          контролы переносятся сами по себе, слева от неё. */}
      {/* ⚠️ На узком экране панель живёт НАВЕРХУ, в одной строке с кнопкой меню
          (см. app-shell): там всё равно оставалась пустая полоса под кнопку.
          Спрятать её здесь классом нельзя — панель держит замок «одна на экран»,
          и скрытая копия забрала бы его себе. */}
      {showTitle && isDesktop && <TopPanel className="ml-auto shrink-0" />}
    </div>
  );
}

/** Класс кнопки-контрола портала — для мест, где элемент не <button>
 *  (например <label> с input[type=file]). Стиль берётся из tabButtonStyle(). */
export const TAB_BTN_CLASS =
  "glass-hover inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-[var(--radius-md)] border px-3 text-[12px] font-semibold transition disabled:opacity-50";

/** Инлайновые цвета кнопки-контрола (см. TAB_BTN_CLASS). */
export function tabButtonStyle(opts?: { primary?: boolean; active?: boolean }): CSSProperties {
  const primary = !!opts?.primary;
  return {
    boxShadow: "none",
    borderColor: primary
      ? "transparent"
      : opts?.active
        ? "color-mix(in srgb, var(--primary) 45%, transparent)"
        : "var(--border)",
    background: primary
      ? "var(--primary)"
      : opts?.active
        ? "color-mix(in srgb, var(--primary) 10%, transparent)"
        : "var(--surface)",
    color: primary ? "var(--primaryText)" : "var(--text)",
  };
}

type TabButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title" | "type"> & {
  /** Всплывающая подсказка (data-tooltip портала). */
  title?: string;
  active?: boolean;
  /** «primary» — главное действие экрана (заливка основным цветом): создать,
   *  запустить синхронизацию. Остальное — «default», нейтральная пилюля. */
  variant?: "default" | "primary";
  children: ReactNode;
  type?: "button" | "submit";
};

/**
 * Кнопка-контрол в шапке вкладки — ЕДИНЫЙ вид для всего портала: «Excel»,
 * «Обновить», «Загрузить», выпадающие списки-триггеры. До 03.08 каждая вкладка
 * рисовала свою (где glow-pill 12px, где glass-hover 11px, где серая рамка), и
 * ряд контролов выглядел по-разному от экрана к экрану.
 *
 * `active` — состояние «включено/открыто» (подсветка основным цветом).
 */
export const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(function TabButton(
  { onClick, disabled, title, active, variant = "default", className, children, type = "button", ...rest },
  ref,
) {
  const primary = variant === "primary";
  return (
    <button
      // ...rest и ref обязательны: кнопку используют как триггер поповера
      // (<PopoverTrigger asChild>), а он передаёт СВОИ обработчики и ref через
      // клон элемента. Без проброса попап «Склады» не открывался.
      {...rest}
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-tooltip={title}
      // tab-btn-primary — чтобы ховер не закрашивал primary-кнопку в белый
      // поверх белого текста (правило .glass-hover:hover в globals.css).
      className={`${TAB_BTN_CLASS} ${primary ? "tab-btn-primary" : ""} ${className ?? ""}`}
      style={tabButtonStyle({ primary, active })}
    >
      {children}
    </button>
  );
});
